import {assertMedicationDoseReady} from '../../../features/medications/medicationDosePresentation';
import {readMedicationDose,formatMedicationDose,prepareMedicationDoseRecord} from '../../../shared/medicationDose';
import { addDoc, collection, deleteDoc, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../../services/firebase";
import {
  buildMedicationVersionSnapshot,
  normalizeMedicationDetail,
  updateMedicationDose,
} from "./childMedicationHelpers";

const formatLocalDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const formatDateLabel = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTimeLabel = (timeValue = "") => {
  const [hours, minutes] = String(timeValue || "").split(":").map((value) => Number(value));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return String(timeValue || "").trim();
  }

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const getScheduleKey = (schedule = {}, index = 0) => (
  schedule.id
  || `${String(schedule.time || "").trim()}|${String(schedule.dose || "").trim()}|${String(schedule.unit || "").trim()}|${index}`
);

const normalizeScheduleSignature = (schedule = {}, index = 0) => ({
  key: getScheduleKey(schedule, index),
  id: schedule.id || "",
  dose: String(schedule.dose || "").trim(),
  unit: String(schedule.unit || "").trim(),
  doseText:readMedicationDose(schedule).doseText,
  time: String(schedule.time || "").trim(),
});

const normalizeMedicationForComparison = (medication = {}) => {
  const normalized = normalizeMedicationDetail(medication);
  const schedules = Array.isArray(normalized.schedules) ? normalized.schedules : [];

  return {
    ...normalized,
    childId: String(medication.childId || "").trim(),
    schedules: schedules.map((schedule, index) => normalizeScheduleSignature(schedule, index)),
  };
};

const mergeMedicationArrayById = (existing = [], additions = []) => {
  const seen = new Set();
  return [...(Array.isArray(existing) ? existing : []), ...(Array.isArray(additions) ? additions : [])]
    .filter((entry) => {
      const key = entry?.id || entry?.sourceId || JSON.stringify(entry);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

const getEffectiveDateValue = (medication = {}) => (
  medication.effectiveFrom
  || medication.startDate
  || medication.savedAt
  || medication.createdAt
  || ""
);

const buildScheduleLabel = (schedule = {}) => {
  const parts = [];
  const timeLabel = formatTimeLabel(schedule.time);
  if (timeLabel) {
    parts.push(timeLabel);
  }

  const doseLabel = formatMedicationDose(schedule);
  if (doseLabel) {
    parts.push(doseLabel);
  }

  return parts.join(" • ");
};

const buildTimelineWriteFields = (text = "") => ({
  text,
  status: "active",
  timestamp: serverTimestamp(),
});

const getTemporaryHoldSignature = (medication = {}) => {
  const hold = medication.temporaryHold;
  if (!hold || typeof hold !== "object") return "";
  return [hold.startDate, hold.endDate, hold.resumeDate, hold.reason]
    .map((value) => String(value || "").trim())
    .join("|");
};

const formatMedicationDateKeyLabel = (dateKey = "") => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || ""))) return String(dateKey || "").trim();
  return formatDateLabel(`${dateKey}T12:00:00`);
};

export const buildMedicationChangeEvent = (previousMedication, nextMedication, { currentUserId } = {}) => {
  const prevMed = previousMedication ? normalizeMedicationForComparison(previousMedication) : null;
  const nextMed = normalizeMedicationForComparison(nextMedication);

  if (!nextMed?.id) {
    return null;
  }

  const now = new Date();
  const effectiveFromValue = getEffectiveDateValue(nextMed);
  const effectiveDate = effectiveFromValue ? new Date(effectiveFromValue) : now;
  const effectiveLabel = formatDateLabel(effectiveDate);
  const effectiveDateKey = formatLocalDateKey(now);
  const scheduledEffectiveLabel = formatDateLabel(effectiveDate) || "today";

  if (!prevMed) {
    const firstSchedule = nextMed.schedules[0] || null;
    return {
      childId: nextMed.childId || "",
      category: "medication_change",
      source: "medication_plan_change",
      medicationId: nextMed.id,
      medicationName: nextMed.name,
      medicationChangeType: "medication_added",
      effectiveFrom: effectiveFromValue || nextMed.startDate || "",
      actualDateTime: now.toISOString(),
      entryDate: effectiveDateKey,
      summary: firstSchedule
        ? `${nextMed.name} added: ${buildScheduleLabel(firstSchedule)}`
        : `${nextMed.name} added`,
      changes: nextMed.schedules.map((schedule, index) => ({
        field: "schedule",
        scheduleId: schedule.id || getScheduleKey(schedule, index),
        label: buildScheduleLabel(schedule),
        from: null,
        to: {
          dose: schedule.dose,
          unit: schedule.unit,
          time: schedule.time,
        },
      })),
      title: "Medication added",
      medicationChangeTitle: "Medication added",
      medicationChangeSummary: firstSchedule
        ? `${nextMed.name}: ${buildScheduleLabel(firstSchedule)}`
        : `${nextMed.name} added`,
      medicationChangeDetails: nextMed.schedules.map((schedule) => buildScheduleLabel(schedule)).filter(Boolean),
      medicationChangeEffectiveLabel: scheduledEffectiveLabel,
      ...buildTimelineWriteFields(firstSchedule
        ? `${nextMed.name} added: ${buildScheduleLabel(firstSchedule)}`
        : `${nextMed.name} added`),
      createdAt: serverTimestamp(),
      createdBy: currentUserId || auth?.currentUser?.uid || null,
    };
  }

  const prevScheduleMap = new Map(prevMed.schedules.map((schedule, index) => [schedule.key || getScheduleKey(schedule, index), schedule]));
  const nextScheduleMap = new Map(nextMed.schedules.map((schedule, index) => [schedule.key || getScheduleKey(schedule, index), schedule]));
  const allKeys = new Set([...prevScheduleMap.keys(), ...nextScheduleMap.keys()]);
  const changes = [];
  let hasDoseChange = false;
  let hasScheduleChange = false;

  const previousHoldSignature = getTemporaryHoldSignature(prevMed);
  const nextHoldSignature = getTemporaryHoldSignature(nextMed);
  if (nextHoldSignature && nextHoldSignature !== previousHoldSignature) {
    const hold = nextMed.temporaryHold;
    const startLabel = formatMedicationDateKeyLabel(hold.startDate);
    const endLabel = formatMedicationDateKeyLabel(hold.endDate);
    const resumeLabel = formatMedicationDateKeyLabel(hold.resumeDate);
    const holdRange = [startLabel, endLabel].filter(Boolean).join("–");
    const summary = [
      `${nextMed.name} temporarily held${holdRange ? ` ${holdRange}` : ""}`,
      resumeLabel ? `resume ${resumeLabel}` : "",
      hold.reason ? `(${hold.reason})` : "",
    ].filter(Boolean).join("; ");

    return {
      childId: nextMed.childId || prevMed.childId || "",
      category: "medication_change",
      source: "medication_plan_change",
      medicationId: nextMed.id,
      medicationName: nextMed.name,
      medicationChangeType: "temporary_hold",
      effectiveFrom: hold.startDate || "",
      actualDateTime: now.toISOString(),
      entryDate: effectiveDateKey,
      summary,
      changes: [{ field: "temporary_hold", from: prevMed.temporaryHold || null, to: hold }],
      title: "Medication temporarily held",
      medicationChangeTitle: "Medication temporarily held",
      medicationChangeSummary: summary,
      medicationChangeDetails: [
        holdRange ? `Hold: ${holdRange}` : "",
        resumeLabel ? `Resume: ${resumeLabel}` : "",
        hold.reason ? `Reason: ${hold.reason}` : "",
      ].filter(Boolean),
      medicationChangeEffectiveLabel: startLabel || scheduledEffectiveLabel,
      ...buildTimelineWriteFields(summary),
      createdAt: serverTimestamp(),
      createdBy: currentUserId || auth?.currentUser?.uid || null,
    };
  }

  allKeys.forEach((key) => {
    const previousSchedule = prevScheduleMap.get(key) || null;
    const nextSchedule = nextScheduleMap.get(key) || null;

    if (previousSchedule && !nextSchedule) {
      hasScheduleChange = true;
      changes.push({
        field: "schedule",
        scheduleId: previousSchedule.id || key,
        label: buildScheduleLabel(previousSchedule),
        from: {
          dose: previousSchedule.dose,
          unit: previousSchedule.unit,
          time: previousSchedule.time,
        },
        to: null,
      });
      return;
    }

    if (!previousSchedule && nextSchedule) {
      hasScheduleChange = true;
      changes.push({
        field: "schedule",
        scheduleId: nextSchedule.id || key,
        label: buildScheduleLabel(nextSchedule),
        from: null,
        to: {
          dose: nextSchedule.dose,
          unit: nextSchedule.unit,
          time: nextSchedule.time,
        },
      });
      return;
    }

    if (!previousSchedule || !nextSchedule) {
      return;
    }

    const doseChanged = previousSchedule.dose !== nextSchedule.dose || previousSchedule.unit !== nextSchedule.unit;
    const timeChanged = previousSchedule.time !== nextSchedule.time;

    if (timeChanged) {
      hasScheduleChange = true;
      changes.push({
        field: "schedule",
        scheduleId: nextSchedule.id || key,
        label: buildScheduleLabel(nextSchedule),
        from: {
          dose: previousSchedule.dose,
          unit: previousSchedule.unit,
          time: previousSchedule.time,
        },
        to: {
          dose: nextSchedule.dose,
          unit: nextSchedule.unit,
          time: nextSchedule.time,
        },
      });
      return;
    }

    if (doseChanged) {
      hasDoseChange = true;
      changes.push({
        field: "dose",
        scheduleId: nextSchedule.id || key,
        label: buildScheduleLabel(nextSchedule),
        from: {
          dose: previousSchedule.dose,
          unit: previousSchedule.unit,
          time: previousSchedule.time,
        },
        to: {
          dose: nextSchedule.dose,
          unit: nextSchedule.unit,
          time: nextSchedule.time,
        },
      });
    }
  });

  if (!changes.length && prevMed.isArchived === nextMed.isArchived && prevMed.name === nextMed.name) {
    return null;
  }

  if (Boolean(nextMed.isArchived) || String(nextMed.effectiveTo || "").trim()) {
    if (nextMed.medicationPlanAction === "complete_course") {
      const completionSummary = `${nextMed.name} course completed`;
      const completionDate = nextMed.completedCourse?.effectiveDate || "";
      return {
        childId: nextMed.childId || prevMed.childId || "",
        category: "medication_change",
        source: "medication_plan_change",
        medicationId: nextMed.id,
        medicationName: nextMed.name,
        medicationChangeType: "course_completed",
        effectiveFrom: nextMed.completedCourse?.effectiveDate || effectiveFromValue || "",
        actualDateTime: now.toISOString(),
        entryDate: /^\d{4}-\d{2}-\d{2}$/.test(completionDate) ? completionDate : effectiveDateKey,
        summary: completionSummary,
        changes,
        title: "Medication course completed",
        medicationChangeTitle: "Medication course completed",
        medicationChangeSummary: completionSummary,
        medicationChangeDetails: nextMed.completionReason ? [`Outcome: ${nextMed.completionReason}`] : [],
        medicationChangeEffectiveLabel: formatMedicationDateKeyLabel(nextMed.completedCourse?.effectiveDate) || scheduledEffectiveLabel,
        ...buildTimelineWriteFields(completionSummary),
        createdAt: serverTimestamp(),
        createdBy: currentUserId || auth?.currentUser?.uid || null,
      };
    }
    const provider = String(nextMed.prescribingDoctor || "").trim();
    const stoppedSummary = `${nextMed.name} is no longer active${provider ? ` per ${provider}` : ""}`;
    return {
      childId: nextMed.childId || prevMed.childId || "",
      category: "medication_change",
      source: "medication_plan_change",
      medicationId: nextMed.id,
      medicationName: nextMed.name,
      medicationChangeType: "medication_stopped",
      effectiveFrom: effectiveFromValue || nextMed.startDate || "",
      actualDateTime: now.toISOString(),
      entryDate: effectiveDateKey,
      summary: stoppedSummary,
      changes,
      title: "Medication stopped",
      medicationChangeTitle: "Medication stopped",
      medicationChangeSummary: `${nextMed.name} no longer active`,
      medicationChangeDetails: provider ? [`Provider: ${provider}`] : [],
      medicationChangeEffectiveLabel: scheduledEffectiveLabel,
      ...buildTimelineWriteFields(stoppedSummary),
      createdAt: serverTimestamp(),
      createdBy: currentUserId || auth?.currentUser?.uid || null,
    };
  }

  if (!changes.length) {
    return null;
  }

  const changeType = hasScheduleChange ? "schedule_changed" : "dose_changed";
  const detailLines = changes
    .slice(0, 4)
    .map((change) => {
      const fromDose = [change.from?.dose, change.from?.unit].filter(Boolean).join(" ").trim();
      const toDose = [change.to?.dose, change.to?.unit].filter(Boolean).join(" ").trim();
      const fromTime = formatTimeLabel(change.from?.time);
      const toTime = formatTimeLabel(change.to?.time);
      if (change.field === "dose") {
        return `${change.label || "Schedule"}: ${fromDose || "—"} → ${toDose || "—"}`;
      }

      const fromText = [fromTime, fromDose].filter(Boolean).join(" • ");
      const toText = [toTime, toDose].filter(Boolean).join(" • ");
      return `${change.label || "Schedule"}: ${fromText || "—"} → ${toText || "—"}`;
    });

  return {
    childId: nextMed.childId || prevMed.childId || "",
    category: "medication_change",
    source: "medication_plan_change",
    medicationId: nextMed.id,
    medicationName: nextMed.name,
    medicationChangeType: changeType,
    effectiveFrom: effectiveFromValue || nextMed.startDate || "",
    actualDateTime: now.toISOString(),
    entryDate: effectiveDateKey,
    summary: `${nextMed.name} updated`,
    changes,
    title: "Medication updated",
    medicationChangeTitle: "Medication updated",
    medicationChangeSummary: `${nextMed.name} updated`,
    medicationChangeDetails: detailLines,
    medicationChangeEffectiveLabel: scheduledEffectiveLabel,
    ...buildTimelineWriteFields(`${nextMed.name} updated`),
    createdAt: serverTimestamp(),
    createdBy: currentUserId || auth?.currentUser?.uid || null,
  };
};

export const saveMedicationRecord = async ({
  childId,
  medication,
  status = "draft",
  previousMedication = null,
  preserveDoseVersion = false,
  createTimelineEvent = true,
  onNonCriticalError,
}) => {
  if (!childId) {
    throw new Error("Child profile is not ready yet.");
  }

  assertMedicationDoseReady(medication);
  const normalized = normalizeMedicationDetail(medication);
  const savedAt = new Date().toISOString();
  const createdBy = auth?.currentUser?.uid;

  if (!createdBy) {
    throw new Error("You must be signed in to save medication details.");
  }

  const medicationRef = doc(db, "medications", normalized.id);
  const shouldReadExistingMedication = Boolean(previousMedication);
  const existingSnapshot = shouldReadExistingMedication ? await getDoc(medicationRef) : null;
  const existingMedication = existingSnapshot && existingSnapshot.exists()
    ? { id: existingSnapshot.id, ...existingSnapshot.data() }
    : null;
  const previousSource = previousMedication || existingMedication || null;
  const previousSnapshot = previousSource ? buildMedicationVersionSnapshot(previousSource) : null;
  const currentEffectiveFrom = normalized.effectiveFrom || normalized.startDate || savedAt;
  const { nextRecord } = preserveDoseVersion && previousSource
    ? { nextRecord: { ...previousSource, ...normalized } }
    : updateMedicationDose({
        medication: { ...(previousSource || {}), ...normalized },
        medicationId: normalized.id,
        nextDose: normalized.dose,
        nextUnit: normalized.unit,
        nextSchedules: normalized.schedules,
        effectiveFrom: currentEffectiveFrom,
        closePreviousVersion: Boolean(previousSnapshot),
        createdBy,
      });

  const record = {
    ...normalized,
    ...nextRecord,
    childId,
    createdBy,
    createdAt: savedAt,
    effectiveFrom: currentEffectiveFrom,
    effectiveTo: normalized.effectiveTo || "",
    isArchived: Boolean(medication?.isArchived),
    archivedAt: medication?.archivedAt || "",
    archivedBy: medication?.archivedBy || "",
    syncStatus: status,
    savedAt,
    updatedAt: savedAt,
  };
  record.genericName = normalized.genericName || nextRecord.genericName || "";
  record.brandName = normalized.brandName || nextRecord.brandName || "";
  record.aliases = Array.isArray(normalized.aliases) ? normalized.aliases : [];
  record.prescribingDoctor = normalized.prescribingDoctor || nextRecord.prescribingDoctor || "";
  record.needsReview = Boolean(normalized.needsReview || nextRecord.needsReview);
  record.reviewReasons = Array.isArray(normalized.reviewReasons) ? normalized.reviewReasons : [];
  record.sourceDocuments = mergeMedicationArrayById(previousSource?.sourceDocuments, normalized.sourceDocuments);
  record.planHistory = mergeMedicationArrayById(previousSource?.planHistory, normalized.planHistory);

  const completeRecord=prepareMedicationDoseRecord(record);
  await setDoc(medicationRef, completeRecord, { merge: true });

  const changeEvent = buildMedicationChangeEvent(previousSource, record, {
    currentUserId: createdBy,
  });

  if (createTimelineEvent && changeEvent) {
    try {
      await addDoc(collection(db, "dailyLogs"), changeEvent);
    } catch (error) {
      console.error("Error writing medication change event:", error);
      onNonCriticalError?.({ stage: "timeline", error });
    }
  }

  return completeRecord;
};

export const saveMedicationTimelineEvent = async ({
  previousMedication = null,
  medication,
}) => {
  const currentUserId = auth?.currentUser?.uid;
  const changeEvent = buildMedicationChangeEvent(previousMedication, medication, {
    currentUserId,
  });

  if (!changeEvent) return null;
  return addDoc(collection(db, "dailyLogs"), changeEvent);
};

export const archiveMedicationRecord = async ({
  childId,
  medication,
  archived = true,
  effectiveDate = '',
  createTimelineEvent = true,
}) => {
  if (!childId) {
    throw new Error("Child profile is not ready yet.");
  }

  assertMedicationDoseReady(medication);
  const normalized = normalizeMedicationDetail(medication);
  const savedAt = new Date().toISOString();
  const effectiveAt = /^\d{4}-\d{2}-\d{2}$/.test(String(effectiveDate || ''))
    ? `${effectiveDate}T12:00:00.000Z`
    : savedAt;
  const archivedBy = auth?.currentUser?.uid;
  const previousSnapshot = buildMedicationVersionSnapshot(normalized);
  const { nextRecord } = updateMedicationDose({
    medication: normalized,
    medicationId: normalized.id,
    nextDose: normalized.dose,
    nextUnit: normalized.unit,
    nextSchedules: normalized.schedules,
    effectiveFrom: normalized.effectiveFrom || normalized.startDate || normalized.createdAt || savedAt,
    closePreviousVersion: true,
    createdBy: archivedBy,
  });

  if (!archivedBy) {
    throw new Error("You must be signed in to update medication details.");
  }

  const record = {
    ...normalized,
    ...nextRecord,
    childId,
    createdBy: normalized.createdBy || archivedBy,
    createdAt: normalized.createdAt || savedAt,
    effectiveFrom: normalized.effectiveFrom || normalized.startDate || normalized.createdAt || savedAt,
    effectiveTo: archived ? effectiveAt : "",
    history: Array.isArray(normalized.history)
      ? [...normalized.history, {
        ...previousSnapshot,
        effectiveTo: previousSnapshot.effectiveTo || effectiveAt,
      }]
      : [{
        ...previousSnapshot,
        effectiveTo: previousSnapshot.effectiveTo || effectiveAt,
      }],
    isArchived: archived,
    archivedAt: archived ? effectiveAt : "",
    archivedBy: archived ? archivedBy : "",
    syncStatus: archived ? "archived" : "saved",
    savedAt: normalized.savedAt || savedAt,
    updatedAt: savedAt,
  };

  const medicationRef = doc(db, "medications", normalized.id);
  const completeRecord=prepareMedicationDoseRecord(record);
  await setDoc(medicationRef, completeRecord, { merge: true });

  const changeEvent = buildMedicationChangeEvent(normalized, record, {
    currentUserId: archivedBy,
  });

  if (createTimelineEvent && changeEvent) {
    await addDoc(collection(db, "dailyLogs"), changeEvent);
  }

  return completeRecord;
};

export const deleteUnfinalizedMedicationRecord = async ({ childId, medicationId }) => {
  if (!childId || !medicationId) {
    throw new Error("Medication review is missing its profile or medication.");
  }

  const medicationRef = doc(db, "medications", medicationId);
  const snapshot = await getDoc(medicationRef);
  if (!snapshot.exists() || snapshot.data()?.childId !== childId) {
    throw new Error("Medication review does not belong to the selected profile.");
  }

  await deleteDoc(medicationRef);
};
